import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ExpenseCategoryTree = ({ onCategorySelect, selectedCategory }) => {
  const [expandedCategories, setExpandedCategories] = useState(['operational', 'client-specific']);

  // Categories structure updated with new expense categories
  const categories = [
    {
      id: 'operational',
      name: 'Despesas Operacionais',
      budget: 0,
      spent: 0,
      icon: 'Building2',
      subcategories: [
        { id: 'advocacia', name: 'Advocacia', budget: 0, spent: 0, icon: 'Scale' },
        { id: 'contabilidade', name: 'Contabilidade', budget: 0, spent: 0, icon: 'Calculator' },
        { id: 'marketing', name: 'Marketing', budget: 0, spent: 0, icon: 'Megaphone' },
        { id: 'seguro', name: 'Seguro', budget: 0, spent: 0, icon: 'Shield' },
        { id: 'hospedagem', name: 'Hospedagem', budget: 0, spent: 0, icon: 'Hotel' }
      ]
    },
    {
      id: 'travel',
      name: 'Viagens e Transporte',
      budget: 0,
      spent: 0,
      icon: 'Plane',
      subcategories: [
        { id: 'aereo', name: 'Aéreo', budget: 0, spent: 0, icon: 'Plane' },
        { id: 'documentacao', name: 'Documentação', budget: 0, spent: 0, icon: 'FileText' }
      ]
    },
    {
      id: 'technology',
      name: 'Tecnologia',
      budget: 0,
      spent: 0,
      icon: 'Laptop',
      subcategories: [
        { id: 'aplicativos', name: 'Aplicativos', budget: 0, spent: 0, icon: 'Smartphone' }
      ]
    },
    {
      id: 'others',
      name: 'Outras Despesas',
      budget: 0,
      spent: 0,
      icon: 'Package',
      subcategories: [
        { id: 'outros', name: 'Outros', budget: 0, spent: 0, icon: 'MoreHorizontal' }
      ]
    }
  ];

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev =>
      prev?.includes(categoryId)
        ? prev?.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const calculatePercentage = (spent, budget) => {
    return Math.min((spent / budget) * 100, 100);
  };

  const getStatusColor = (percentage) => {
    if (percentage >= 90) return 'bg-error';
    if (percentage >= 75) return 'bg-warning';
    return 'bg-success';
  };

  return (
    <div className="h-full flex flex-col bg-card border-r border-border">
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="FolderTree" size={20} />
          Categorias
        </h2>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {categories?.map((category) => {
          const isExpanded = expandedCategories?.includes(category?.id);
          const percentage = calculatePercentage(category?.spent, category?.budget);

          return (
            <div key={category?.id} className="mb-2">
              <button
                onClick={() => toggleCategory(category?.id)}
                className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors text-left"
              >
                <div className="flex items-center gap-2 flex-1">
                  <Icon name={isExpanded ? 'ChevronDown' : 'ChevronRight'} size={16} />
                  <Icon name={category?.icon} size={18} />
                  <span className="text-sm font-medium text-foreground">{category?.name}</span>
                </div>
              </button>
              <div className="px-3 mt-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>R$ {category?.spent?.toLocaleString('pt-BR')}</span>
                  <span>R$ {category?.budget?.toLocaleString('pt-BR')}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all ${getStatusColor(percentage)}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
              {isExpanded && (
                <div className="ml-6 mt-2 space-y-1">
                  {category?.subcategories?.map((sub) => {
                    const subPercentage = calculatePercentage(sub?.spent, sub?.budget);
                    const isSelected = selectedCategory === sub?.id;

                    return (
                      <button
                        key={sub?.id}
                        onClick={() => onCategorySelect(sub?.id)}
                        className={`w-full p-2 rounded-md transition-colors text-left ${
                          isSelected ? 'bg-primary/10 border border-primary' : 'hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon name={sub?.icon} size={16} />
                          <span className="text-sm text-foreground">{sub?.name}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                          <span>R$ {sub?.spent?.toLocaleString('pt-BR')}</span>
                          <span className={subPercentage >= 90 ? 'text-error font-medium' : ''}>
                            {subPercentage?.toFixed(0)}%
                          </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-1">
                          <div
                            className={`h-1 rounded-full transition-all ${getStatusColor(subPercentage)}`}
                            style={{ width: `${subPercentage}%` }}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="p-4 border-t border-border bg-muted/50">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Orçado:</span>
            <span className="font-semibold text-foreground">
              R$ {categories?.reduce((sum, cat) => sum + cat?.budget, 0)?.toLocaleString('pt-BR')}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Gasto:</span>
            <span className="font-semibold text-foreground">
              R$ {categories?.reduce((sum, cat) => sum + cat?.spent, 0)?.toLocaleString('pt-BR')}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Disponível:</span>
            <span className="font-semibold text-success">
              R$ {(categories?.reduce((sum, cat) => sum + cat?.budget, 0) - categories?.reduce((sum, cat) => sum + cat?.spent, 0))?.toLocaleString('pt-BR')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseCategoryTree;